import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableFooter from '@mui/material/TableFooter';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import update from 'immutability-helper';

interface IProps {
  maxWidth?: DialogProps['maxWidth'];
  onCancel: () => void;
  onSubmit: (tempHtml: {
    header: string[][];
    body: string[][];
    footer: string[][];
  }) => void;
  visible: boolean;
  tableData?: {
    header: string[][];
    body: string[][];
    footer: string[][];
  };
}

const useStyles = makeStyles((theme) => ({
  tableHeaderCell: {
    backgroundColor: theme.palette.mode === 'light' ? '#e6e6e6' : '#2a2a2a',
  },
  tableBodyCell: {
    backgroundColor: theme.palette.mode === 'light' ? '#f9f9f9' : '#525252',
  },
  tableFooterCell: {
    backgroundColor: theme.palette.mode === 'light' ? '#e6e6e6' : '#2a2a2a',
  },
}));

export default function ModalAddEditTable(props: IProps) {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    tableData: propsTableData,
    visible,
    onCancel,
    maxWidth,
    onSubmit,
  } = props;
  const [headerRowCount, setHeaderRowCount] = useState<number>(
    propsTableData && propsTableData.header ? propsTableData.header.length : 1
  );
  const [footerRowCount, setFooterRowCount] = useState<number>(
    propsTableData && propsTableData.footer ? propsTableData.footer.length : 1
  );
  const [rowCount, setRowCount] = useState<number>(
    propsTableData && propsTableData.body ? propsTableData.body.length : 3
  );
  // @ts-ignore
  const [columnCount, setColumnCount] = useState<number>(
    propsTableData && propsTableData.body && propsTableData.body.length
      ? propsTableData.body[0].length
      : 3
  );
  const [tableData, setTableData] = useState<{
    header: string[][];
    body: string[][];
    footer: string[][];
  }>(
    propsTableData || {
      header: [],
      body: [],
      footer: [],
    }
  );

  function syncTableDataWithRowColumnCount() {
    let tableDataHeader = tableData.header;
    let tableDataBody = tableData.body;
    let tableDataFooter = tableData.footer;

    // tableDataHeader
    if (tableDataHeader.length > headerRowCount) {
      tableDataHeader = tableDataHeader.slice(0, headerRowCount);
    } else if (tableDataHeader.length < headerRowCount) {
      const appendData = new Array(
        headerRowCount - tableDataHeader.length
      ).fill(new Array(columnCount).fill(''));

      tableDataHeader = [...tableDataHeader, ...appendData];
    }

    tableDataHeader = tableDataHeader.map((rowData) => {
      if (rowData.length > columnCount) {
        rowData = rowData.slice(0, columnCount);
      } else if (rowData.length < columnCount) {
        const appendData = new Array(columnCount - rowData.length).fill('');

        rowData = [...rowData, ...appendData];
      }
      return rowData;
    });

    // tableDataFooter
    if (tableDataFooter.length > footerRowCount) {
      tableDataFooter = tableDataFooter.slice(0, footerRowCount);
    } else if (tableDataFooter.length < footerRowCount) {
      const appendData = new Array(
        footerRowCount - tableDataFooter.length
      ).fill(new Array(columnCount).fill(''));

      tableDataFooter = [...tableDataFooter, ...appendData];
    }

    tableDataFooter = tableDataFooter.map((rowData) => {
      if (rowData.length > columnCount) {
        rowData = rowData.slice(0, columnCount);
      } else if (rowData.length < columnCount) {
        const appendData = new Array(columnCount - rowData.length).fill('');

        rowData = [...rowData, ...appendData];
      }
      return rowData;
    });

    // tableDataBody
    if (tableDataBody.length > rowCount) {
      tableDataBody = tableDataBody.slice(0, rowCount);
    } else if (tableDataBody.length < rowCount) {
      const appendData = new Array(rowCount - tableDataBody.length).fill(
        new Array(columnCount).fill('')
      );

      tableDataBody = [...tableDataBody, ...appendData];
    }

    tableDataBody = tableDataBody.map((rowData) => {
      if (rowData.length > columnCount) {
        rowData = rowData.slice(0, columnCount);
      } else if (rowData.length < columnCount) {
        const appendData = new Array(columnCount - rowData.length).fill('');

        rowData = [...rowData, ...appendData];
      }
      return rowData;
    });
    setTableData({
      header: tableDataHeader,
      body: tableDataBody,
      footer: tableDataFooter,
    });
  }

  useEffect(() => {
    syncTableDataWithRowColumnCount();
  }, []);

  function drawTable() {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            {tableData.header.map((trData, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={index}>
                {trData.map((tdData, index2) => (
                  <TableCell
                    // eslint-disable-next-line react/no-array-index-key
                    key={index2}
                    padding="none"
                    className={classes.tableHeaderCell}
                  >
                    <TextField
                      value={tdData}
                      variant="outlined"
                      fullWidth
                      onChange={(e) =>
                        setTableData((tempTableData) =>
                          update(tempTableData, {
                            header: {
                              [index]: { [index2]: { $set: e.target.value } },
                            },
                          })
                        )
                      }
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {tableData.body.map((trData, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={index}>
                {trData.map((tdData, index2) => (
                  <TableCell
                    // eslint-disable-next-line react/no-array-index-key
                    key={index2}
                    padding="none"
                    className={classes.tableBodyCell}
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={tdData}
                      onChange={(e) => {
                        setTableData((tempTableData) =>
                          update(tempTableData, {
                            body: {
                              [index]: { [index2]: { $set: e.target.value } },
                            },
                          })
                        );
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            {tableData.footer.map((trData, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={index}>
                {trData.map((tdData, index2) => (
                  <TableCell
                    // eslint-disable-next-line react/no-array-index-key
                    key={index2}
                    padding="none"
                    className={classes.tableFooterCell}
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={tdData}
                      onChange={(e) => {
                        setTableData((tempTableData) =>
                          update(tempTableData, {
                            footer: {
                              [index]: { [index2]: { $set: e.target.value } },
                            },
                          })
                        );
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableFooter>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Dialog open={visible} onClose={onCancel} maxWidth={maxWidth} fullWidth>
      <DialogTitle>{propsTableData ? t('edit') : t('add')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid container item spacing={1}>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label={t('table_header_row')}
                type="number"
                value={headerRowCount}
                inputProps={{ min: '0' }}
                onChange={(e) => {
                  setHeaderRowCount(Number(e.target.value));
                }}
                onBlur={syncTableDataWithRowColumnCount}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label={t('table_body_row')}
                type="number"
                value={rowCount}
                inputProps={{ min: '1' }}
                onChange={(e) => {
                  setRowCount(Number(e.target.value));
                }}
                onBlur={syncTableDataWithRowColumnCount}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label={t('table_footer_row')}
                type="number"
                value={footerRowCount}
                inputProps={{ min: '0' }}
                onChange={(e) => {
                  setFooterRowCount(Number(e.target.value));
                }}
                onBlur={syncTableDataWithRowColumnCount}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label={t('table_column')}
                type="number"
                value={columnCount}
                inputProps={{ min: '1' }}
                onChange={(e) => {
                  setColumnCount(Number(e.target.value));
                }}
                onBlur={syncTableDataWithRowColumnCount}
              />
            </Grid>
          </Grid>
          <Grid container item>
            <Grid item xs={12}>
              *{t('enter_same_data_to_header_will_merge_header')}
            </Grid>
            {drawTable()}
          </Grid>
          <Grid container item justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button onClick={onCancel}>{t('cancel')}</Button>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  onSubmit(tableData);
                }}
              >
                {propsTableData ? t('save') : t('add')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
